import { ReactNode, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

export interface ToastProps {
  id: string;
  variant: ToastVariant;
  title?: string;
  message: string | ReactNode;
  duration?: number;
  onClose: (id: string) => void;
}

/**
 * Toast - Notificación con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - Container: shadow-lg con ring inset sutil
 * - Dark mode: bg-gray-800 con ring-white/10
 * - Close button: hover:text-white en dark
 * - Transiciones animadas
 */
const variantClasses: Record<ToastVariant, { bg: string; icon: string; iconPath: string }> = {
  success: {
    bg: cn(
      'bg-white shadow-lg',
      'ring-1 ring-inset ring-black/5',
      'dark:bg-secondary-800 dark:ring-white/10'
    ),
    icon: 'text-success-500 dark:text-success-400',
    iconPath: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  error: {
    bg: cn(
      'bg-white shadow-lg',
      'ring-1 ring-inset ring-black/5',
      'dark:bg-secondary-800 dark:ring-white/10'
    ),
    icon: 'text-danger-500 dark:text-danger-400',
    iconPath: 'M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  warning: {
    bg: cn(
      'bg-white shadow-lg',
      'ring-1 ring-inset ring-black/5',
      'dark:bg-secondary-800 dark:ring-white/10'
    ),
    icon: 'text-warning-500 dark:text-warning-400',
    iconPath: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
  },
  info: {
    bg: cn(
      'bg-white shadow-lg',
      'ring-1 ring-inset ring-black/5',
      'dark:bg-secondary-800 dark:ring-white/10'
    ),
    icon: 'text-info-500 dark:text-info-400',
    iconPath: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
  },
};

export function Toast({ id, variant, title, message, duration = 5000, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);
  const classes = variantClasses[variant];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose(id), 300); // Wait for animation
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, id, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300);
  };

  return (
    <div
      className={cn(
        // Base: full width on mobile, fixed width on larger screens
        'pointer-events-auto w-[calc(100vw-3rem)] sm:w-auto sm:min-w-[360px] max-w-md',
        'overflow-hidden rounded-xl',
        'transition-all duration-300 ease-out',
        classes.bg,
        isVisible 
          ? 'animate-in slide-in-from-right-full fade-in duration-300' 
          : 'animate-out slide-out-to-right-full fade-out duration-200'
      )}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <svg 
              className={cn('size-5', classes.icon)} 
              fill="none" 
              viewBox="0 0 24 24" 
              strokeWidth={2} 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d={classes.iconPath} />
            </svg>
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {title && (
              <p className="text-sm font-semibold text-secondary-900 dark:text-white">
                {title}
              </p>
            )}
            <p className={cn(
              'text-sm text-secondary-600 dark:text-secondary-300',
              title && 'mt-0.5'
            )}>
              {message}
            </p>
          </div>
          
          {/* Close button */}
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className={cn(
                'inline-flex items-center justify-center rounded-lg p-1.5 -m-1.5',
                'text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100',
                'dark:text-secondary-500 dark:hover:text-white dark:hover:bg-white/10',
                'transition-colors duration-150',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
              )}
            >
              <span className="sr-only">Cerrar</span>
              <svg className="size-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Toast Container
export interface ToastContainerProps {
  position?: ToastPosition;
  children: ReactNode;
}

const positionClasses: Record<ToastPosition, string> = {
  'top-right': 'top-0 right-0',
  'top-left': 'top-0 left-0',
  'bottom-right': 'bottom-0 right-0',
  'bottom-left': 'bottom-0 left-0',
  'top-center': 'top-0 left-1/2 -translate-x-1/2',
  'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2',
};

export function ToastContainer({ position = 'top-right', children }: ToastContainerProps) {
  return (
    <div
      aria-live="assertive"
      className={cn(
        'pointer-events-none fixed z-[100] flex flex-col gap-3',
        'p-4 sm:p-6', // Less padding on mobile
        positionClasses[position]
      )}
    >
      {children}
    </div>
  );
}
